import './GadgetsLibraryDrawer.css';
import type {
  GadgetManifestEntry,
  GadgetCategory,
} from '../../services/host/GadgetsServiceModule';
import type {
  GadgetsLibraryState,
  GadgetsLibraryTab,
} from '../../hooks/useGadgetsLibrary';

/**
 * GadgetsLibraryDrawer — 2026-06-08 Phase 3E wire-up for the canonical
 * Q Manual VG35002 gadgets catalog.
 *
 * 3-tab layout: Common / Security / Exotic. Each tab renders the matching
 * gadgets grouped by their fine-grained category (the 13 seed categories
 * cluster cleanly into the 3 super-categories — Phase 3A audit
 * confirmation block).
 *
 * Detail expansion: name + Q Manual page citation + description + Q
 * evaluation + game information. Gadget stat blocks are heterogeneous
 * (different gadgets carry different stats_schema kinds — non_combat,
 * armor_modifier, weapon_stats, etc.) so no PM/RED/CRUS-style grid like
 * vehicles. Rules + contents arrays are surfaced as readable lists when
 * present.
 *
 * Mirrors VehiclesLibraryDrawer.tsx pattern (Phase 2E) but with single-
 * manifest hook + no companion file.
 */

const TAB_DEFS: ReadonlyArray<{ key: GadgetsLibraryTab; label: string }> = [
  { key: 'common', label: 'Common' },
  { key: 'security', label: 'Security' },
  { key: 'exotic', label: 'Exotic' },
];

interface InnerGroupDef {
  category: GadgetCategory;
  label: string;
}

const COMMON_GROUPS: ReadonlyArray<InnerGroupDef> = [
  { category: 'attache_case', label: 'Attache Cases' },
  { category: 'belt', label: 'Belts' },
  { category: 'pen', label: 'Pens' },
  { category: 'shoe', label: 'Shoes' },
  { category: 'umbrella', label: 'Umbrellas' },
  { category: 'watch', label: 'Watches' },
  { category: 'miscellaneous_common_device', label: 'Miscellaneous Common' },
];

const SECURITY_GROUPS: ReadonlyArray<InnerGroupDef> = [
  { category: 'search_id_device', label: 'Search & Identification' },
  { category: 'surveillance_device', label: 'Surveillance Bugs' },
  { category: 'counter_surveillance_device', label: 'Counter-Surveillance' },
  { category: 'sensor_detector', label: 'Sensors & Detectors' },
  { category: 'tracer', label: 'Tracers' },
];

const EXOTIC_GROUPS: ReadonlyArray<InnerGroupDef> = [
  { category: 'exotic_device', label: 'Exotic Devices' },
];

const TAB_TO_GROUPS: Record<GadgetsLibraryTab, ReadonlyArray<InnerGroupDef>> = {
  common: COMMON_GROUPS,
  security: SECURITY_GROUPS,
  exotic: EXOTIC_GROUPS,
};

const TAB_TO_CATEGORIES: Record<GadgetsLibraryTab, Set<GadgetCategory>> = {
  common: new Set(COMMON_GROUPS.map((g) => g.category)),
  security: new Set(SECURITY_GROUPS.map((g) => g.category)),
  exotic: new Set(EXOTIC_GROUPS.map((g) => g.category)),
};

export interface GadgetsLibraryDrawerProps {
  gadgets: GadgetManifestEntry[];
  state: GadgetsLibraryState;
  error?: string | null;
  activeTab: GadgetsLibraryTab;
  onTabChange: (tab: GadgetsLibraryTab) => void;
  selectedGadgetId: string | null;
  selectedGadgetData: Record<string, unknown> | null;
  selectedGadgetState: GadgetsLibraryState;
  onSelectGadget: (gadgetId: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

function GadgetDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const description = (data['description'] as string) ?? '';
  const qEvaluation = (data['q_evaluation'] as string) ?? '';
  const gameInformation = (data['game_information'] as string) ?? '';
  const page = data['page'] as number | undefined;
  const manifestSection = data['manifest_section'] as string | undefined;
  const contents = data['contents'] as string[] | undefined;

  return (
    <div className="br-gadgets-detail" data-testid={`${testidPrefix}-detail`}>
      <h3 className="br-gadgets-detail-title">
        {(data['name'] as string) ?? '—'}
      </h3>
      {(page !== undefined || manifestSection !== undefined) && (
        <p
          className="br-gadgets-detail-page br-label"
          data-testid={`${testidPrefix}-detail-page`}
        >
          {manifestSection !== undefined && <>§{manifestSection} · </>}
          {page !== undefined && <>p.{page}</>}
        </p>
      )}
      {description !== '' && (
        <p
          className="br-gadgets-detail-text"
          data-testid={`${testidPrefix}-detail-description`}
        >
          {description}
        </p>
      )}
      {qEvaluation !== '' && (
        <p
          className="br-gadgets-detail-text br-gadgets-detail-q"
          data-testid={`${testidPrefix}-detail-q-evaluation`}
        >
          <em>Q evaluation:</em> {qEvaluation}
        </p>
      )}
      {gameInformation !== '' && (
        <p
          className="br-gadgets-detail-text br-gadgets-detail-game"
          data-testid={`${testidPrefix}-detail-game-info`}
        >
          <strong>Game info:</strong> {gameInformation}
        </p>
      )}
      {contents !== undefined && contents.length > 0 && (
        <div
          className="br-gadgets-detail-contents"
          data-testid={`${testidPrefix}-detail-contents`}
        >
          <strong>Contents:</strong>
          <ul>
            {contents.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function GadgetsLibraryDrawer({
  gadgets,
  state,
  error,
  activeTab,
  onTabChange,
  selectedGadgetId,
  selectedGadgetData,
  selectedGadgetState,
  onSelectGadget,
  onClose,
  testidPrefix = 'br-gadgets-library',
}: GadgetsLibraryDrawerProps) {
  const allowedCategories = TAB_TO_CATEGORIES[activeTab];
  const tabGadgets = gadgets.filter((g) => allowedCategories.has(g.category));
  const groups = TAB_TO_GROUPS[activeTab];

  return (
    <aside
      className="br-scenario-drawer br-gadgets-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
      data-active-tab={activeTab}
    >
      <header className="br-gadgets-library-header">
        <span className="br-gadgets-library-title">Gadgets Library</span>
        <span
          className="br-gadgets-library-count br-label"
          data-testid={`${testidPrefix}-count`}
        >
          {tabGadgets.length}
        </span>
        {onClose !== undefined && (
          <button
            type="button"
            className="br-gadgets-library-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close gadgets library"
          >
            ✕
          </button>
        )}
      </header>

      <div
        className="br-gadgets-library-tabs"
        role="tablist"
        data-testid={`${testidPrefix}-tabs`}
      >
        {TAB_DEFS.map((tabDef) => {
          const isActive = tabDef.key === activeTab;
          return (
            <button
              key={tabDef.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`${testidPrefix}-tab-${tabDef.key}`}
              data-active={isActive ? 'true' : 'false'}
              className={
                isActive
                  ? 'br-gadgets-library-tab br-gadgets-library-tab-active'
                  : 'br-gadgets-library-tab'
              }
              onClick={() => onTabChange(tabDef.key)}
            >
              {tabDef.label}
            </button>
          );
        })}
      </div>

      <p
        className="br-gadgets-library-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Q Manual VG35002 gadgets catalog — 59 entries across Common (26),
        Security (22), and Exotic (11) devices. Click a row to expand its
        description + Q evaluation + game info + contents.
      </p>

      {state === 'loading' && (
        <p
          className="br-gadgets-library-loading"
          data-testid={`${testidPrefix}-loading`}
        >
          Loading gadgets…
        </p>
      )}

      {state === 'error' && (
        <p
          className="br-gadgets-library-error"
          data-testid={`${testidPrefix}-error`}
        >
          Failed to load gadgets: {error ?? 'unknown error'}
        </p>
      )}

      {state === 'ready' && tabGadgets.length === 0 && (
        <p
          className="br-gadgets-library-empty"
          data-testid={`${testidPrefix}-empty`}
        >
          No {activeTab} gadgets in seed.
        </p>
      )}

      {state === 'ready' && tabGadgets.length > 0 && (
        <div
          className="br-gadgets-library-list"
          data-testid={`${testidPrefix}-list`}
        >
          {groups.map((groupDef) => {
            const items = tabGadgets.filter(
              (g) => g.category === groupDef.category,
            );
            if (items.length === 0) return null;
            return (
              <section
                key={groupDef.category}
                className="br-gadgets-library-group"
                data-category={groupDef.category}
                data-testid={`${testidPrefix}-group-${groupDef.category}`}
              >
                <h4 className="br-gadgets-library-group-title">
                  {groupDef.label} ({items.length})
                </h4>
                <ul className="br-gadgets-library-rows">
                  {items.map((g) => {
                    const isSelected = g.id === selectedGadgetId;
                    return (
                      <li
                        key={g.id}
                        className="br-gadgets-library-row-wrapper"
                      >
                        <button
                          type="button"
                          className={
                            isSelected
                              ? 'br-gadgets-library-row br-gadgets-library-row-selected'
                              : 'br-gadgets-library-row'
                          }
                          data-testid={`${testidPrefix}-row-${g.id}`}
                          data-selected={isSelected ? 'true' : 'false'}
                          onClick={() => onSelectGadget(g.id)}
                          aria-pressed={isSelected}
                        >
                          <span className="br-gadgets-library-row-title">
                            {g.name}
                          </span>
                          {(g.manifest_section !== null ||
                            g.page !== null) && (
                            <span className="br-gadgets-library-row-meta br-label">
                              {g.manifest_section !== null && (
                                <>§{g.manifest_section}</>
                              )}
                              {g.page !== null && <> · p.{g.page}</>}
                            </span>
                          )}
                        </button>
                        {isSelected && selectedGadgetState === 'loading' && (
                          <p
                            className="br-gadgets-library-detail-loading"
                            data-testid={`${testidPrefix}-detail-loading`}
                          >
                            Loading…
                          </p>
                        )}
                        {isSelected && selectedGadgetState === 'error' && (
                          <p
                            className="br-gadgets-library-detail-error"
                            data-testid={`${testidPrefix}-detail-error`}
                          >
                            Could not load gadget.
                          </p>
                        )}
                        {isSelected &&
                          selectedGadgetState === 'ready' &&
                          selectedGadgetData !== null && (
                            <GadgetDetail
                              data={selectedGadgetData}
                              testidPrefix={testidPrefix}
                            />
                          )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </aside>
  );
}
